import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

// EK-442 — preferências de voz (TTS) do modo de conversa por voz com o Líder.
// Persistidas em localStorage seguindo o mesmo padrão do ThemeContext.

export interface VoicePreferences {
	/** Velocidade da fala do TTS (0.5–2.0, padrão 1.0). */
	rate: number;
	/** Tom/pitch da fala (0–2, padrão 1.0). */
	pitch: number;
	/** Volume da fala (0–1, padrão 1.0). */
	volume: number;
	/** voiceURI escolhido pelo usuário; null = voz pt-BR padrão do sistema. */
	voiceURI: string | null;
	/**
	 * Quando true, permite que o usuário interrompa a fala da IA falando por cima
	 * (barge-in). Default `false` porque o cancelamento de eco do navegador
	 * raramente é perfeito e pode disparar interrupções falsas com o próprio áudio
	 * do TTS — priorizamos ouvir o feedback completo da IA.
	 */
	bargeIn: boolean;
}

interface VoicePreferencesContextValue extends VoicePreferences {
	setRate: (rate: number) => void;
	setPitch: (pitch: number) => void;
	setVolume: (volume: number) => void;
	setVoiceURI: (voiceURI: string | null) => void;
	setBargeIn: (bargeIn: boolean) => void;
	reset: () => void;
}

const STORAGE_KEYS = {
	rate: "ekeeps-voice-rate",
	pitch: "ekeeps-voice-pitch",
	volume: "ekeeps-voice-volume",
	voiceURI: "ekeeps-voice-uri",
	bargeIn: "ekeeps-voice-barge-in",
} as const;

export const DEFAULT_VOICE_PREFERENCES: VoicePreferences = {
	rate: 1.0,
	pitch: 1.0,
	volume: 1.0,
	voiceURI: null,
	bargeIn: false,
};

const VoicePreferencesContext = createContext<VoicePreferencesContextValue | undefined>(undefined);

function getStoredNumber(key: string, fallback: number, min: number, max: number): number {
	try {
		const raw = localStorage.getItem(key);
		if (raw === null) return fallback;
		const parsed = Number(raw);
		if (!Number.isFinite(parsed)) return fallback;
		return Math.min(max, Math.max(min, parsed));
	} catch {
		return fallback;
	}
}

function getStoredString(key: string, fallback: string | null): string | null {
	try {
		const raw = localStorage.getItem(key);
		return raw ?? fallback;
	} catch {
		return fallback;
	}
}

function getStoredBoolean(key: string, fallback: boolean): boolean {
	try {
		const raw = localStorage.getItem(key);
		if (raw === null) return fallback;
		return raw === "true";
	} catch {
		return fallback;
	}
}

export const VoicePreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [rate, setRateState] = useState<number>(() => getStoredNumber(STORAGE_KEYS.rate, DEFAULT_VOICE_PREFERENCES.rate, 0.5, 2));
	const [pitch, setPitchState] = useState<number>(() => getStoredNumber(STORAGE_KEYS.pitch, DEFAULT_VOICE_PREFERENCES.pitch, 0, 2));
	const [volume, setVolumeState] = useState<number>(() => getStoredNumber(STORAGE_KEYS.volume, DEFAULT_VOICE_PREFERENCES.volume, 0, 1));
	const [voiceURI, setVoiceURIState] = useState<string | null>(() => getStoredString(STORAGE_KEYS.voiceURI, DEFAULT_VOICE_PREFERENCES.voiceURI));
	const [bargeIn, setBargeInState] = useState<boolean>(() => getStoredBoolean(STORAGE_KEYS.bargeIn, DEFAULT_VOICE_PREFERENCES.bargeIn));

	const setRate = useCallback((value: number) => {
		const clamped = Math.min(2, Math.max(0.5, value));
		setRateState(clamped);
		localStorage.setItem(STORAGE_KEYS.rate, String(clamped));
	}, []);

	const setPitch = useCallback((value: number) => {
		const clamped = Math.min(2, Math.max(0, value));
		setPitchState(clamped);
		localStorage.setItem(STORAGE_KEYS.pitch, String(clamped));
	}, []);

	const setVolume = useCallback((value: number) => {
		const clamped = Math.min(1, Math.max(0, value));
		setVolumeState(clamped);
		localStorage.setItem(STORAGE_KEYS.volume, String(clamped));
	}, []);

	const setVoiceURI = useCallback((value: string | null) => {
		setVoiceURIState(value);
		if (value === null) {
			localStorage.removeItem(STORAGE_KEYS.voiceURI);
		} else {
			localStorage.setItem(STORAGE_KEYS.voiceURI, value);
		}
	}, []);

	const setBargeIn = useCallback((value: boolean) => {
		setBargeInState(value);
		localStorage.setItem(STORAGE_KEYS.bargeIn, value ? "true" : "false");
	}, []);

	const reset = useCallback(() => {
		setRate(DEFAULT_VOICE_PREFERENCES.rate);
		setPitch(DEFAULT_VOICE_PREFERENCES.pitch);
		setVolume(DEFAULT_VOICE_PREFERENCES.volume);
		setVoiceURI(DEFAULT_VOICE_PREFERENCES.voiceURI);
		setBargeIn(DEFAULT_VOICE_PREFERENCES.bargeIn);
	}, [setRate, setPitch, setVolume, setVoiceURI, setBargeIn]);

	const value = useMemo<VoicePreferencesContextValue>(
		() => ({ rate, pitch, volume, voiceURI, bargeIn, setRate, setPitch, setVolume, setVoiceURI, setBargeIn, reset }),
		[rate, pitch, volume, voiceURI, bargeIn, setRate, setPitch, setVolume, setVoiceURI, setBargeIn, reset],
	);

	return <VoicePreferencesContext.Provider value={value}>{children}</VoicePreferencesContext.Provider>;
};

export const useVoicePreferences = (): VoicePreferencesContextValue => {
	const ctx = useContext(VoicePreferencesContext);
	if (!ctx) {
		throw new Error("useVoicePreferences must be used within a VoicePreferencesProvider");
	}
	return ctx;
};
